/**
 * One vertical's block in the every-vertical Equipment Tracker (api#1136):
 * its units by status and the four needing a look first, as sent.
 *
 * Counts are the API's, in its order, labelled in each vertical's own words
 * (overview-ops-labels); an unknown status reads "—". Pressure testing counts
 * calibration states, not unit states. Attention rows carry the API's reason
 * as written. A vertical that FAILED says so in place of its counts, never a
 * fleet of zeros; one that cannot be stated shows — and the reason.
 */
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { equipmentStatusLabel, equipmentTitle } from '../../lib/overview-ops-labels';
import type { VerticalEquipment } from '../../types/overview-ops';

export function VerticalEquipmentBlock({ v }: { v: VerticalEquipment }) {
  const navigate = useNavigate();
  const title = v.status === 'failed' ? equipmentTitle(v.vertical) : v.label || equipmentTitle(v.vertical);

  let body: React.ReactNode;
  if (v.status === 'failed') {
    body = <p data-testid="equipment-failed" className="text-xs text-fieldiq-muted">Couldn’t load these. This is not an empty list.</p>;
  } else if (v.status === 'not_computable') {
    body = <p data-testid="equipment-not-computable" className="text-xs text-fieldiq-muted">—{v.reason ? ` ${v.reason}` : ''}</p>;
  } else if (v.total === 0) {
    body = <p data-testid="equipment-vertical-empty" className="text-xs text-fieldiq-muted">None on file.</p>;
  } else {
    body = (
      <>
        <ul data-testid={`equipment-counts-${v.vertical}`} className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {v.counts.map((c) => (
            <li key={c.status} className="text-fieldiq-muted">
              {equipmentStatusLabel(v.vertical, c.status)} <span className="font-bold tabular-nums text-fieldiq-text">{c.count}</span>
            </li>
          ))}
          <li className="text-fieldiq-muted">
            Total <span className="font-bold tabular-nums text-fieldiq-text">{v.total}</span>
          </li>
        </ul>
        {v.attention.length > 0 && (
          <ul data-testid={`equipment-attention-${v.vertical}`} className="mt-2 divide-y divide-fieldiq-border rounded-fi-control border border-fieldiq-border">
            {v.attention.map((a) => (
              <li key={a.id} className="flex items-baseline justify-between gap-3 px-3 py-2 text-xs">
                <span className="min-w-0 truncate font-semibold text-fieldiq-text">
                  {[a.identifier, a.name].filter(Boolean).join(' · ') || '—'}
                </span>
                <span className="shrink-0 text-fieldiq-muted">{a.reason}</span>
              </li>
            ))}
          </ul>
        )}
      </>
    );
  }

  return (
    <section data-testid={`equipment-block-${v.vertical}`} className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-xs font-bold uppercase tracking-wide text-fieldiq-text">{title}</h3>
        {v.link && v.status !== 'failed' && (
          <button
            type="button"
            onClick={() => navigate(v.link)}
            className="inline-flex items-center gap-0.5 text-xs font-semibold text-fieldiq-text underline decoration-fi-amber decoration-2 underline-offset-4"
          >
            Open <ChevronRight className="size-3.5" aria-hidden="true" />
          </button>
        )}
      </div>
      {body}
    </section>
  );
}
